// src/components/DCS/DCSQuestionnaire.tsx
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Button, Form, ProgressBar, Alert, Table, Badge, Toast, ToastContainer,
} from 'react-bootstrap';
import { client } from '../../lib/AmplifyClient';
import type { Schema } from '../../../amplify/data/resource';
import { useUserContext } from '../Context/UserContext';
import { saveDCS } from '../../lib/profile';

type Issue = Schema['Issue']['type'];

/** ----------------------------
 * Dfuse Conflict Styles (DCS) v2
 * - 20 items, 4 per mode
 * - Half workplace, half family
 * ---------------------------- */
type Mode = 'Avoidance' | 'Competition' | 'Compromise' | 'Accommodation' | 'Collaboration';
type Ctx = 'work' | 'family';

const ITEMS: { text: string; mode: Mode; context: Ctx }[] = [
  // AVOIDANCE
  { text: 'I hold back my concerns in meetings to avoid derailing progress.', mode: 'Avoidance', context: 'work' },
  { text: 'I prefer to leave small work disagreements unspoken.', mode: 'Avoidance', context: 'work' },
  { text: 'I change the subject when relatives bring up conflict.', mode: 'Avoidance', context: 'family' },
  { text: 'I stay quiet to prevent arguments at home.', mode: 'Avoidance', context: 'family' },

  // COMPETITION
  { text: 'I argue strongly for my position in team debates.', mode: 'Competition', context: 'work' },
  { text: 'I use authority or expertise to get decisions made quickly.', mode: 'Competition', context: 'work' },
  { text: 'I push to get my way in family disputes.', mode: 'Competition', context: 'family' },
  { text: 'I insist on my preferences when choices matter to me.', mode: 'Competition', context: 'family' },

  // COMPROMISE
  { text: 'I propose splitting options so the team can move on.', mode: 'Compromise', context: 'work' },
  { text: 'I trade less important work issues to reach agreement.', mode: 'Compromise', context: 'work' },
  { text: 'I suggest sharing chores or responsibilities equally.', mode: 'Compromise', context: 'family' },
  { text: 'I look for ways each person can give up something small.', mode: 'Compromise', context: 'family' },

  // ACCOMMODATION
  { text: 'I adjust my approach to support my manager’s decisions.', mode: 'Accommodation', context: 'work' },
  { text: 'I step back when a coworker feels strongly about an issue.', mode: 'Accommodation', context: 'work' },
  { text: 'I put others’ needs first to keep peace at home.', mode: 'Accommodation', context: 'family' },
  { text: 'I agree to plans I don’t prefer to avoid arguments.', mode: 'Accommodation', context: 'family' },

  // COLLABORATION
  { text: 'I encourage colleagues to share concerns so we can solve them together.', mode: 'Collaboration', context: 'work' },
  { text: 'I seek creative solutions that work for everyone on the team.', mode: 'Collaboration', context: 'work' },
  { text: 'I talk through family issues until we all feel satisfied.', mode: 'Collaboration', context: 'family' },
  { text: 'I combine ideas with loved ones to build long-lasting solutions.', mode: 'Collaboration', context: 'family' },
];

const ITEM_WEIGHTS: number[] = Array.from({ length: ITEMS.length }, () => 1);

const QUESTIONS: string[] = ITEMS.map(i => i.text);
const INDEX = (i: number) => i + 1;

const GROUPS = (() => {
  const g: Record<Mode, number[]> = {
    Avoidance: [], Competition: [], Compromise: [], Accommodation: [], Collaboration: [],
  };
  ITEMS.forEach((item, idx) => { g[item.mode].push(INDEX(idx)); });
  return g as Readonly<typeof g>;
})();

type StyleKey = keyof typeof GROUPS;

/** 4 items per mode (max 20) */
function classify(score: number): "Very strong" | "Strong" | "Average" | "Weak" | "Very weak" {
  if (score >= 17) return "Very strong";
  if (score >= 13) return "Strong";
  if (score >= 9)  return "Average";
  if (score >= 5)  return "Weak";
  return "Very weak";
}

function ResultBadge({ label, score }: { label: string; score: number }) {
  const band = classify(score);
  const variant =
    band === "Very strong" ? "success" :
    band === "Strong"      ? "primary" :
    band === "Average"     ? "secondary" :
    band === "Weak"        ? "warning" : "dark";
  return (
    <Badge bg={variant} className="ms-2" title={`${label}: ${band}`}>
      {band}
    </Badge>
  );
}

const ASSESSMENT_VERSION = 'DCS-v2';

/** Map questionnaire labels → lowercase snapshot/profile keys */
const MODE_TO_SNAPSHOT: Record<Mode, 'avoiding' | 'competing' | 'compromising' | 'accommodating' | 'collaborating'> = {
  Avoidance: 'avoiding',
  Competition: 'competing',
  Compromise: 'compromising',
  Accommodation: 'accommodating',
  Collaboration: 'collaborating',
};

// minimal update helper with auth-mode fallback (writes should normally be userPool)
async function updateModelWithFallback<M = any>(modelName: string, input: any): Promise<M | null> {
  try {
    const r1 = await (client as any).models[modelName].update(input, { authMode: 'userPool' });
    if (r1?.data) return r1.data as M;
  } catch {}
  try {
    const r2 = await (client as any).models[modelName].update(input, { authMode: 'apiKey' });
    return (r2?.data ?? null) as M | null;
  } catch {
    return null;
  }
}

export default function DCSQuestionnaire() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { firstName, email, sub } = useUserContext();

  const inStandalone = !id;

  const [issue, setIssue] = useState<Issue | null>(null);
  const [role, setRole] = useState<'initiator' | 'collaborator' | 'unknown'>('unknown');
  const [loadingRole, setLoadingRole] = useState(!inStandalone);

  const [answers, setAnswers] = useState<number[]>(Array.from({ length: 20 }, () => 0));
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load Issue + infer my role ONLY when we have an id
  useEffect(() => {
    let mounted = true;
    if (!id) {
      // Standalone: no error, no Issue lookup
      setIssue(null);
      setRole('unknown');
      setLoadingRole(false);
      return () => { mounted = false; };
    }

    (async () => {
      try {
        const res = await client.models.Issue.get({ id });
        if (!mounted) return;
        const data = (res?.data ?? null) as Issue | null;
        setIssue(data);

        let inferred: 'initiator' | 'collaborator' | 'unknown' = 'unknown';
        if (data && email) {
          const e = email.toLowerCase();
          if (data.initiatorEmail?.toLowerCase() === e) inferred = 'initiator';
          else if (data.collaboratorEmail?.toLowerCase() === e) inferred = 'collaborator';
        }
        setRole(inferred);
      } catch (e: any) {
        setError(e?.message ?? 'Failed to load session');
      } finally {
        if (mounted) setLoadingRole(false);
      }
    })();

    return () => { mounted = false; };
  }, [id, email]);

  // Progress
  const progress = useMemo(() => {
    const answered = answers.filter(v => v > 0).length;
    return Math.round((answered / 20) * 100);
  }, [answers]);

  // Totals by mode
  const totals = useMemo(() => {
    const sum = (idxs: readonly number[]) =>
      idxs.reduce((acc, qi) => {
        const i = qi - 1;
        const val = answers[i] || 0;
        const w = ITEM_WEIGHTS[i] || 1;
        return acc + val * w;
      }, 0);
    return {
      Avoidance:    sum(GROUPS.Avoidance),
      Competition:  sum(GROUPS.Competition),
      Compromise:   sum(GROUPS.Compromise),
      Accommodation:sum(GROUPS.Accommodation),
      Collaboration:sum(GROUPS.Collaboration),
    };
  }, [answers]);

  // Dominant (ties allowed)
  const dominant = useMemo(() => {
    const entries = Object.entries(totals) as [StyleKey, number][];
    const max = Math.max(...entries.map(([, v]) => v));
    const top = entries.filter(([, v]) => v === max).map(([k]) => k);
    return { top, max };
  }, [totals]);

  const canSubmit = progress === 100 && !submitted;

  // For Profile storage (full breakdown)
  const resultArray = useMemo(() => {
    return (Object.keys(totals) as StyleKey[]).map((k) => {
      const score = (totals as any)[k] as number;
      return `${k}: ${score} (${classify(score)})`;
    });
  }, [totals]);

  async function handleSave() {
    try {
      setSaving(true);
      setError(null);

      // 1) dominant style
      const myDominantLabel = (dominant.top[0] ?? '') as Mode;
      if (!myDominantLabel) throw new Error('Could not determine dominant style.');
      const snapshotValue = MODE_TO_SNAPSHOT[myDominantLabel];

      // 2) Profile updates (always)
      if (sub && email) {
        await saveDCS({
          userId: sub,
          email,
          dcsScores: resultArray,
          dcsVersion: ASSESSMENT_VERSION,
        });

        const emailLower = email.trim().toLowerCase();
        await updateModelWithFallback<Schema['Profile']['type']>('Profile', {
          emailLower,
          dcsMode: snapshotValue,
        });
      }

      // 3) If we're in a session context, WE DO NOT update the Issue anymore,
      //    we just navigate to the next step.
      if (id && issue?.id) {
        // [REMOVED] Issue update logic
        // We still log for debugging purposes, but no database write to 'Issue' occurs.
        console.log('[DCSQuestionnaire][POST-SAVE ROUTE] Profile updated, skipping Issue update.');
        navigate(`/session/${encodeURIComponent(id)}/implement`);
      }

      setShowToast(true);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save results.');
    } finally {
      setSaving(false);
    }
  }

  const displayName = firstName ?? email ?? 'there';
  const disableSave = saving || (id ? (loadingRole || role === 'unknown') : false);

  return (
    <Container className="py-4">
      <Row className="mb-1">
        <Col>
          <h3 className="mb-1">20-Question Conflict Styles Assessment</h3>
          <div className="text-muted">
            Welcome, <strong>{displayName}</strong>
            {!inStandalone && !loadingRole && role !== 'unknown' && (
              <> &middot; Your role: <strong>{role === 'initiator' ? 'Initiator' : 'Collaborator'}</strong></>
            )}
            {inStandalone && <span className="ms-2 small text-muted">(standalone)</span>}
          </div>
          <div className="small text-muted mt-2">
            Answer once. Your results are tied to your account and can be used across Accordia sessions.
          </div>
        </Col>
      </Row>

      {/* Only show API errors (not the old missing-id message) */}
      {error && (
        <Row className="mb-3"><Col><Alert variant="danger">{error}</Alert></Col></Row>
      )}

      <Row className="mb-3">
        <Col>
          <ProgressBar now={progress} label={`${progress}%`} />
          <div className="small text-muted mt-1">
            1 = never, 2 = seldom, 3 = sometimes, 4 = often, 5 = always
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              <Form>
                {QUESTIONS.map((q, i) => (
                  <Card key={i} className="mb-3 border-0 border-top">
                    <Card.Body className="pb-2">
                      <div className="fw-semibold mb-1">
                        {INDEX(i)}. {q}
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        {[1,2,3,4,5].map(val => (
                          <Form.Check
                            key={val}
                            inline
                            type="radio"
                            name={`q-${i}`}
                            id={`q-${i}-${val}`}
                            label={val.toString()}
                            checked={answers[i] === val}
                            onChange={() => setAnswers(prev => {
                              const next = [...prev];
                              next[i] = val;
                              return next;
                            })}
                          />
                        ))}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </Form>

              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  disabled={!canSubmit}
                  onClick={() => setSubmitted(true)}
                >
                  Show Results
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => { setAnswers(Array(20).fill(0)); setSubmitted(false); }}
                >
                  Reset
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {submitted && (
        <Row className="mt-4">
          <Col>
            <Card className="shadow-sm">
              <Card.Body>
                <h5 className="mb-3">Your Scores</h5>
                <Table responsive hover className="align-middle">
                  <thead>
                    <tr>
                      <th>Style</th>
                      <th>Questions</th>
                      <th>Score</th>
                      <th>Band</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Object.keys(GROUPS) as StyleKey[]).map((k) => {
                      const score = (totals as any)[k] as number;
                      return (
                        <tr key={k}>
                          <td><strong>{k}</strong></td>
                          <td className="text-muted">{GROUPS[k].join(', ')}</td>
                          <td>{score}</td>
                          <td><ResultBadge label={k} score={score} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                <Alert variant="info" className="mt-3">
                  <div className="fw-semibold mb-1">
                    Dominant style{dominant.top.length > 1 ? 's' : ''}: {dominant.top.join(', ')} ({dominant.max})
                  </div>
                  <div className="small text-muted">
                    (Per style: max 20) — Very strong: 17–20 · Strong: 13–16 · Average: 9–12 · Weak: 5–8 · Very weak: 0–4
                  </div>
                </Alert>

                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    disabled={disableSave}
                    onClick={handleSave}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Show a Home button only when opened standalone (from Home) */}
      {inStandalone && (
        <Row className="mt-4">
          <Col className="d-flex justify-content-end">
            <Button variant="outline-secondary" onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </Col>
        </Row>
      )}

      <ToastContainer position="top-end" className="p-3">
        <Toast bg="success" onClose={() => setShowToast(false)} show={showToast} delay={4000} autohide>
          <Toast.Header>
            <strong className="me-auto">Saved</strong>
          </Toast.Header>
          <Toast.Body className="text-white">
            Your <strong>Profile.dcsMode</strong> was updated and full scores stored (version {ASSESSMENT_VERSION}).
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
}